// memberList — the 3-container normaliser (contract 22 §9 `service/`), pure and framework-free.
//
// v3 wrote this rule TWICE: an 83-line normaliser inside `UsersPermissions.vue:509-587` and a
// second, simplified twin inside `NotificationUpdateModal.vue:245-266`. Contract §6.2 row 10 names
// both call sites. Three v4 panes consume it (ORP-18888 Permissions, ORP-18886 Notifications,
// ORP-18889 invite modal), so it lives here, once, with the rules tested rather than re-derived.
//
// No Vue, no Pinia, no `shared/http`: every input arrives as an argument.
import {
  MEMBER_SCOPE_LEVEL,
  asInviteId,
  asUserId,
  type AccountMemberRow,
  type AccountUserDto,
  type AccountUsersDto,
  type AssistantSeats,
  type MemberDashboardRef,
  type MemberListRow,
  type MemberRole,
  type MemberScope,
  type MemberWorkspaceRef,
  type PendingInviteDto,
  type PendingInviteRow,
  type WorkspaceWithPermissionsDto,
} from '../models/member.types'

/** `UserAccessPermission.Role` → role name. Mapped BY NAME: v3 mapped these positionally into a
 *  `roles` array (`UsersPermissions.vue:531-532, 555-556`), so adding a role silently reassigned
 *  every dashboard grant (analysis §12, BE-22-08). */
const ROLE_BY_ACCESS_LEVEL: Readonly<Record<number, MemberRole>> = {
  10: 'viewer',
  15: 'viewer_plus',
  20: 'editor',
  30: 'admin',
}

const MEMBER_ROLES: readonly string[] = ['viewer', 'viewer_plus', 'editor', 'admin', 'owner']

const HOURS_24_MS = 24 * 60 * 60 * 1000

/** The comparison form of an email. Exported so a caller testing "is this address already here?"
 *  (ORP-18889's duplicate detection) folds candidates exactly the way this module folded rows. */
export function foldEmail(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

/** Ids cross this boundary as both numbers and strings (route params, JSON, 07's store). */
function sameId(a: unknown, b: unknown): boolean {
  return a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b)
}

function idSet(ids: readonly (number | string)[] | undefined): ReadonlySet<string> {
  return new Set((ids ?? []).map((id) => String(id)))
}

function roleFromName(value: unknown): MemberRole | null {
  const name = foldEmail(value)
  return MEMBER_ROLES.includes(name) ? (name as MemberRole) : null
}

function roleFromAccessLevel(value: unknown): MemberRole | null {
  return typeof value === 'number' ? (ROLE_BY_ACCESS_LEVEL[value] ?? null) : null
}

function displayEmail(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function displayName(value: unknown): string | null {
  const name = typeof value === 'string' ? value.trim() : ''
  return name === '' ? null : name
}

/**
 * `permission_type` → scope. The backend stores the uppercase enum name
 * (`Permission.<X>.name`, `accounts/views.py:608-622`); folded here because one write path sends
 * it lowercase (`views.py:481`). Anything unrecognised is an ACCOUNT invite, matching v3's `else`.
 */
export function inviteScope(permissionType: unknown): MemberScope {
  switch (foldEmail(permissionType)) {
    case 'workspace':
      return 'workspace'
    case 'dashboard':
      return 'dashboard'
    default:
      return 'account'
  }
}

/**
 * The 24-hour invite boundary (`isOlderThan24Hours`, v3 `composables/format.js:679-687`, called at
 * `UsersPermissions.vue:522`). `>=`, not `>` — exactly 24 h old is expired, as in v3. An
 * unparseable or missing `created_at` is NOT expired: a row you cannot date is not a row you may
 * disable. `now` is injectable so the boundary is testable without faking the clock.
 */
export function isInviteExpired(createdAt: unknown, now: number | Date = Date.now()): boolean {
  if (typeof createdAt !== 'string' || createdAt.trim() === '') return false
  const created = new Date(createdAt).getTime()
  if (Number.isNaN(created)) return false
  return (now instanceof Date ? now.getTime() : now) - created >= HOURS_24_MS
}

export interface MemberListContext {
  /** The selected workspace. Invites and workspace grants are scoped to it (v3 `:527, :534, :541`). */
  workspaceId: number | string | null | undefined
  /** The account owner's email (07's `useAccount().accountEmail`) — owner-to-top + the `owner` role. */
  ownerEmail?: string | null | undefined
  /**
   * The dashboard catalog, owned by 09 and fetched per workspace (`custom-dashboard/definitions/`,
   * CM-22-03 / BE-22-06). **`undefined` ≠ `[]`**: `[]` means "the account has no dashboards", and
   * dashboard-scoped invites are then correctly filtered out; `undefined` means "not loaded", and
   * those invites are KEPT with an unresolved `dashboards: []` rather than silently vanishing.
   * That distinction is what stops the D14 fan-out failure from emptying the member list.
   */
  dashboards?: readonly MemberDashboardRef[] | undefined
  now?: number | Date | undefined
}

/** Workspace refs for every workspace on the account — `views.py:367` returns all of them. */
function workspaceRefs(workspaces: readonly WorkspaceWithPermissionsDto[]): MemberWorkspaceRef[] {
  return workspaces.map((ws) => ({
    id: ws.id,
    label: typeof ws.label === 'string' ? ws.label : '',
  }))
}

/** Every workspace on which `userId` holds a grant (v3 `:543-549`, minus its always-true clause). */
function grantedWorkspaces(
  workspaces: readonly WorkspaceWithPermissionsDto[],
  userId: number,
): MemberWorkspaceRef[] {
  return workspaceRefs(
    workspaces.filter((ws) =>
      (ws.workspace_permissions ?? []).some((perm) => perm.user_id === userId),
    ),
  )
}

/**
 * Flatten `account_users` + `pending_invites` + the selected workspace's `workspace_permissions`
 * into one list, owner first.
 *
 * ⚠ **Legacy defect D20 is NOT ported.** v3 filtered `account_users` with
 * `if (d.id !== accountStore.accountId)` (`UsersPermissions.vue:514`) — a **user** id compared
 * against an **account** id, i.e. two different tables. It drops exactly the member whose user pk
 * happens to equal the current account pk, at random, and does nothing otherwise. It cannot have
 * meant "hide the owner" (the owner is re-found by email 60 lines later and pinned to the top,
 * `:576-586`) and it cannot have meant "hide me" (that would make the same branch dead).
 *
 * What it was reaching for is **de-duplication**: the backend puts an account-scoped member in
 * `account_users` AND, if they also hold a workspace grant, in `workspace_permissions`, so v3
 * renders that person twice. v3's own second call site does exactly this and has no numeric
 * filter — it de-dupes (`NotificationUpdateModal.vue:250, 257`). So: de-dupe the USER id space,
 * first container wins (account grant ⇒ the broader scope), and drop the id comparison entirely.
 *
 * Invites are never de-duped against members: an invite is a different entity with a different id
 * space and its own revoke lifecycle, and hiding it would hide the only way to cancel it. Callers
 * that need the overlap read `existingEmails` (`useMembers`).
 */
export function buildMemberList(
  dto: AccountUsersDto | null | undefined,
  ctx: MemberListContext,
): MemberListRow[] {
  const workspaces = dto?.workspaces ?? []
  const seenUserIds = new Set<number>()

  return sortOwnerFirst(
    [
      ...accountRows(dto?.account_users ?? [], seenUserIds),
      ...inviteRows(dto?.pending_invites ?? [], workspaces, ctx),
      ...workspaceRows(workspaces, ctx, seenUserIds),
    ],
    ctx.ownerEmail,
  )
}

/** Container 1 — `account_users`, always account scope (`views.py:341-366`). */
function accountRows(
  users: readonly AccountUserDto[],
  seenUserIds: Set<number>,
): AccountMemberRow[] {
  const rows: AccountMemberRow[] = []
  for (const user of users) {
    if (typeof user?.id !== 'number' || seenUserIds.has(user.id)) continue
    seenUserIds.add(user.id)
    rows.push({
      kind: 'member',
      userId: asUserId(user.id),
      email: displayEmail(user.email),
      name: displayName(user.name),
      // `get_permissions` is `'admin' | 'editor' | 'viewer'` only (`helper.py:63`); absent for the
      // owner, who has no account access record (`views.py:353-379`) and is re-roled by the sort.
      role: roleFromName(user.get_account_permissions?.get_permissions) ?? 'viewer',
      scope: 'account',
      scopeLevel: MEMBER_SCOPE_LEVEL.account,
      workspaces: [],
      dashboards: [],
      mcpEnabled: user.mcp_enabled === true,
      isOwner: false,
    })
  }
  return rows
}

/** The workspaces/dashboards an invite covers, or `null` when it belongs to another workspace. */
function inviteTargets(
  invite: PendingInviteDto,
  scope: MemberScope,
  workspaces: readonly WorkspaceWithPermissionsDto[],
  ctx: MemberListContext,
): { workspaces: MemberWorkspaceRef[]; dashboards: MemberDashboardRef[] } | null {
  const entityIds = idSet(invite.entity_ids)

  if (scope === 'workspace') {
    const refs = workspaceRefs(workspaces).filter((ws) => entityIds.has(String(ws.id)))
    if (!refs.some((ws) => sameId(ws.id, ctx.workspaceId))) return null
    return { workspaces: refs, dashboards: [] }
  }

  // A DASHBOARD invite's dashboard ids live in `entity_ids`, not `custom_dashboard_ids` —
  // `PermissionInvite.objects.create` never writes the latter (`views.py:721-728`). With no
  // catalog loaded the invite is kept unresolved rather than dropped (see `MemberListContext`).
  if (scope === 'dashboard' && ctx.dashboards !== undefined) {
    const refs = ctx.dashboards.filter((dash) => entityIds.has(String(dash.id)))
    if (!refs.some((dash) => sameId(dash.workspace_id, ctx.workspaceId))) return null
    return { workspaces: [], dashboards: refs }
  }

  return { workspaces: [], dashboards: [] }
}

/** Container 2 — `pending_invites`, filtered to the selected workspace (v3 `:520-538`). */
function inviteRows(
  invites: readonly PendingInviteDto[],
  workspaces: readonly WorkspaceWithPermissionsDto[],
  ctx: MemberListContext,
): PendingInviteRow[] {
  const rows: PendingInviteRow[] = []
  for (const invite of invites) {
    if (typeof invite?.id !== 'number') continue
    const scope = inviteScope(invite.permission_type)
    const targets = inviteTargets(invite, scope, workspaces, ctx)
    if (targets === null) continue

    rows.push({
      kind: 'invite',
      inviteId: asInviteId(invite.id),
      email: displayEmail(invite.email),
      name: null,
      // ⚠ `permission` for EVERY scope. v3 skipped it for DASHBOARD invites (`:537`) and fell
      // through to a magic-id branch reading `d.role` (`:531-532`) — but `PermissionInvite` has no
      // `role` column (`user_account_configs.py:589-620`), so that branch always resolved to
      // `viewer` and every dashboard-scoped invite rendered "Read Only". Not ported.
      role: roleFromName(invite.permission) ?? 'viewer',
      scope,
      scopeLevel: MEMBER_SCOPE_LEVEL[scope],
      workspaces: targets.workspaces,
      dashboards: targets.dashboards,
      status: typeof invite.status === 'string' ? invite.status : '',
      createdAt: typeof invite.created_at === 'string' ? invite.created_at : '',
      isExpired: isInviteExpired(invite.created_at, ctx.now),
      mcpEnabled: invite.mcp_enabled === true,
    })
  }
  return rows
}

/** Container 3 — the SELECTED workspace's `workspace_permissions` (v3 `:541-542`). */
function workspaceRows(
  workspaces: readonly WorkspaceWithPermissionsDto[],
  ctx: MemberListContext,
  seenUserIds: Set<number>,
): AccountMemberRow[] {
  const selected = workspaces.find((workspace) => sameId(workspace.id, ctx.workspaceId))
  const rows: AccountMemberRow[] = []

  for (const perm of selected?.workspace_permissions ?? []) {
    if (typeof perm?.user_id !== 'number' || seenUserIds.has(perm.user_id)) continue
    seenUserIds.add(perm.user_id)
    const dashboardIds = idSet(perm.dashboard_ids)
    const scope: MemberScope = dashboardIds.size > 0 ? 'dashboard' : 'workspace'
    rows.push({
      kind: 'member',
      userId: asUserId(perm.user_id),
      email: displayEmail(perm.user_email),
      name: null,
      // The raw int FIRST: it is the only source that can express `viewer_plus`, because
      // `format_role_for_frontend` collapses 15 → `'viewer'` (`helper.py:63-71`).
      role: roleFromAccessLevel(perm.role) ?? roleFromName(perm.get_permissions) ?? 'viewer',
      scope,
      scopeLevel: MEMBER_SCOPE_LEVEL[scope],
      workspaces: scope === 'workspace' ? grantedWorkspaces(workspaces, perm.user_id) : [],
      dashboards: (ctx.dashboards ?? []).filter((dash) => dashboardIds.has(String(dash.id))),
      mcpEnabled: perm.mcp_enabled === true,
      isOwner: false,
    })
  }
  return rows
}

/**
 * The owner is re-roled and pinned to the top (v3 `:576-586`). Everything else keeps container
 * order — `Array.prototype.sort` is stable, which is what v3's `return 0` comparator relied on.
 * Only an accepted member can be the owner; a pending invite never is.
 */
function sortOwnerFirst(rows: MemberListRow[], ownerEmail: unknown): MemberListRow[] {
  const owner = foldEmail(ownerEmail)
  if (owner === '') return rows

  for (const row of rows) {
    if (row.kind !== 'member' || foldEmail(row.email) !== owner) continue
    row.isOwner = true
    row.role = 'owner'
    row.scope = 'account'
    row.scopeLevel = MEMBER_SCOPE_LEVEL.account
  }

  return rows.sort((a, b) => rank(a) - rank(b))
}

const rank = (row: MemberListRow): 0 | 1 => (row.kind === 'member' && row.isOwner ? 0 : 1)

/** The account's assistant seats, or `null` when the payload carries none. A non-numeric or negative
 *  `limit` reads as "no ceiling" — the backend fails open on an unmodelled limit, and so does this. */
export function assistantSeats(dto: AccountUsersDto | null | undefined): AssistantSeats | null {
  const seats = dto?.assistant_seats
  if (!seats) return null
  const limit = typeof seats.limit === 'number' && seats.limit >= 0 ? seats.limit : null
  const used = typeof seats.used === 'number' && seats.used >= 0 ? seats.used : 0
  return { limit, used }
}

/** Every address already on the account — accepted members AND pending invites. What ORP-18889's
 *  invite modal tests a typed address against, folded so the comparison is case-insensitive. */
export function existingEmails(rows: readonly MemberListRow[]): Set<string> {
  const emails = new Set<string>()
  for (const row of rows) {
    const email = foldEmail(row.email)
    if (email !== '') emails.add(email)
  }
  return emails
}
